import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import {
  ICustomer,
  IProductDescription
} from '../../../../../store/ducks/customers/types';
import { updateLongSteelShoppingCartSettings } from '../../../../../store/ducks/long-steel-shopping-cart/actions';
import { Container, SearchItemInputContainer } from './styles';
import { useHistory } from 'react-router-dom';
import { ERoutes } from '../../../../../routes';
import { EPurchaseRoutes } from '../../../purchase.routes';
import { EOrderSettingsRoutes } from '../orderSettings.routes';
import {
  removeSearchedItem,
  faceliftSearchItem
} from '../../../../../store/ducks/long-steel-order-item-search/actions';
import SearchItemRadioSelect from './SearchItemRadioSelect';
import {
  ECodeType,
  IOrderEntryPreference
} from '../../../../../store/ducks/user/types';
import { Button } from '@gerdau/hefesto/Button';
import { SearchField } from '@gerdau/hefesto/SearchField';
import FooterButtonContainer from './FooterButtonContainer';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';

interface ISearchItemProps {
  emitterId: string;
  userOrderEntryPreference: IOrderEntryPreference;
  updateUserOrderEntryPreference(
    userOrderEntryPreference: IOrderEntryPreference
  ): void;
}

export interface ISearchItem {
  id: string;
  label: string;
}

const SearchItem: React.FC<ISearchItemProps> = ({
  emitterId,
  userOrderEntryPreference,
  updateUserOrderEntryPreference
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const orderItemSearch = useSelector(
    (state: ApplicationState) => state.longSteelOrderItemSearch
  );

  const [customer, setCustomer] = useState<ICustomer>();
  const [codeType, setCodeType] = useState<ECodeType>();
  const [marketingProducts, setMarketingProducts] = useState<ISearchItem[]>([]);
  const [searchItemArray] = useState<ISearchItem[]>([]);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [lastActionButton, setLastActionButton] = useState('');

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    document.location.pathname
  );

  const updateSearchItems = (searchItems: ISearchItem[]) => {
    if (!customer || !searchItems.length) {
      return;
    }

    dispatch(
      faceliftSearchItem(
        emitterId,
        searchItems.map((searchTerm) => searchTerm?.id).join('\n'),
        codeType ?? ECodeType.SKU,
        customer.locality.state
      )
    );
  };

  const search = (actionButton: string) => {
    if (lastActionButton === 'search' && actionButton === 'searchAdvance') {
      setLastActionButton(actionButton);
      return;
    }

    if (!customer) {
      return;
    }

    if (searchInputValue !== '') {
      updateSearchItems([
        ...searchItemArray,
        { id: searchInputValue, label: searchInputValue }
      ]);

      setSearchInputValue('');
    } else {
      dispatch(
        faceliftSearchItem(
          emitterId,
          searchItemArray.map((searchTerm) => searchTerm?.id).join('\n'),
          codeType ?? ECodeType.SKU,
          customer.locality.state
        )
      );
      updateSearchItems([]);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedValue: string[] = e.clipboardData
      .getData('Text')
      .replace(/\n/g, ' ')
      .replace(/,/g, ' ')
      .replace(/;/g, ' ')
      .split(' ');

    const newSearchTermArray: ISearchItem[] = [...searchItemArray];
    pastedValue.forEach((item) => {
      if (item.trim() !== '') {
        newSearchTermArray.push({ id: item, label: item });
      }
    });
    updateSearchItems([...newSearchTermArray]);
  };

  const onInputKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (searchInputValue === '') {
      if ((e.keyCode === 8 || e.keyCode === 46) && searchItemArray.length > 0) {
        updateSearchItems(
          searchItemArray.splice(0, searchItemArray.length - 1)
        );
      }
      return;
    } else if (searchInputValue.split(' ').length > 1) {
      return;
    }

    switch (e.keyCode) {
      // teclas que disparam a procura
      case 13: // enter
      case 9: // tab
        e.preventDefault();
        if (searchInputValue.trim() !== '') {
          updateSearchItems([
            ...searchItemArray,
            { id: searchInputValue, label: searchInputValue }
          ]);
          setSearchInputValue('');
        }
        break;
    }
  };

  const loadCustomer = () => {
    const customer = customersState.customers.find(
      (customer) => customer.customerId === emitterId
    ) as ICustomer;

    const codeType = customer?.productsDescriptionResponse
      ?.customerProductsAvailable.productsAvailable
      ? userOrderEntryPreference?.codeType
      : ECodeType.SKU;
    setCodeType(codeType);
    setCustomer(customer);
  };

  useEffect(() => {
    dispatch(
      sendTagging({
        page: document.location.pathname,
        screenName,
        event: 'page_view'
      })
    );
  }, []);

  useEffect(() => {
    if (customersState.error) {
      goToOrderSettings();

      return;
    }

    loadCustomer();

    if (
      orderItemSearch.searchedItems.length > 0 &&
      !orderItemSearch.searchedItems.some((item) => !item.found) &&
      lastActionButton === 'searchAdvance'
    ) {
      goToShoppingCart();
    }
  });

  useEffect(() => {
    includeMarketingDescriptionProducts();
  }, [codeType, customer, customersState.loadingProductDescription]);

  const handleAllProducts = useCallback(
    (products: IProductDescription[]) => {
      const result: ISearchItem[] = products
        .filter((product: IProductDescription) => {
          if (codeType === ECodeType.SKU && product.productGerdau) return true;
          if (codeType === ECodeType.CLIENT_SKU && product.productCustomer)
            return true;
          return false;
        })
        .map((product: IProductDescription) => {
          const idProduct =
            codeType === ECodeType.SKU
              ? product.productGerdau.toString()
              : product.productCustomer;
          return {
            id: idProduct,
            label: `${idProduct} - `.concat(product.productDescription)
          };
        });
      setMarketingProducts(result);
    },
    [codeType]
  );

  const includeMarketingDescriptionProducts = () => {
    if (customer?.productsDescriptionResponse?.products) {
      const { products: customerProducts } =
        customer.productsDescriptionResponse;
      handleAllProducts(customerProducts);
    }
  };

  const goToOrderSettings = () => {
    history.push(`${ERoutes.PURCHASE}/${EPurchaseRoutes.LONG_STEEL}`);
  };

  const goToShoppingCart = () => {
    history.push(
      `${ERoutes.PURCHASE}/${EPurchaseRoutes.LONG_STEEL}/${EOrderSettingsRoutes.SHOPPING_CART}`
    );
    const updatedItems = [
      ...shoppingCartState.items,
      ...orderItemSearch.searchedItems
    ];
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        items: updatedItems
      })
    );
    dispatch(removeSearchedItem([]));
  };

  return (
    <>
      <Container>
        {!customersState.loading &&
          customersState.customers.length > 0 &&
          customer?.productsDescriptionResponse?.customerProductsAvailable && (
            <SearchItemRadioSelect
              disabled={orderItemSearch.searchingItems}
              codeType={userOrderEntryPreference.codeType}
              setCodeType={(codeType: ECodeType) => {
                if (userOrderEntryPreference.codeType === codeType) {
                  return;
                }
                updateSearchItems([]);
                updateUserOrderEntryPreference({
                  ...userOrderEntryPreference,
                  codeType
                });
              }}
            />
          )}

        <div className="search-items-content">
          <header>
            <h4>Adicione os itens</h4>
            <span className="search-items-description">
              Adicione pesquisando pela sua descrição ou inserindo o código de
              SKU da sua empresa ou da Gerdau.
            </span>
          </header>

          <SearchItemInputContainer id="searchItemInputContainer">
            <div
              className={orderItemSearch.searchingItems ? 'loading' : ''}
              onClick={() =>
                (document.getElementById('search-input') as HTMLElement).focus()
              }
            >
              <SearchField
                data-testid="hefesto-search-field"
                label="Pesquisar produtos"
                name="search-input"
                inputValue={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                searchResultOptions={marketingProducts}
                onPaste={onPaste}
                onKeyDown={onInputKeydown}
                onSelectResultOption={(selectedItem: ISearchItem) => {
                  dispatch(
                    sendTagging({
                      itemName: 'button:adicionar',
                      itemDescription: `${itemDescription}_adicionar`,
                      screenName,
                      filterInfo: `#codEmr:${emitterId ?? 'not_set'}#codRbd:${
                        shoppingCartState.receiverId
                          ? shoppingCartState.receiverId
                          : 'not_set'
                      }#tpCod:${
                        codeType === 'SKU' ? 'cod_gerdau' : 'cod_interno'
                      }#flt:${searchInputValue}`,
                      event: 'click',
                      product: selectedItem.id,
                      orderCliente: shoppingCartState.customerOrder
                        ? shoppingCartState.customerOrder
                        : 'not_set'
                    })
                  );
                  updateSearchItems([...searchItemArray, selectedItem]);
                  setSearchInputValue('');
                }}
                placeholder={
                  searchItemArray?.length
                    ? ''
                    : 'Pesquise por descrição do produto ou código SKU da Gerdau.'
                }
                disabled={
                  !emitterId ||
                  orderItemSearch.searchingItems ||
                  customersState.loadingProductDescription
                }
              />
            </div>

            <div className="search-items-button">
              <Button
                type="button"
                name="search"
                variant="secondary"
                labelPosition="center"
                onClick={() => {
                  const codMat = searchItemArray
                    .map((searchTerm) => searchTerm?.id)
                    .join('\n');

                  dispatch(
                    sendTagging({
                      itemName: 'button:pesquisar_produtos',
                      itemDescription:
                        'comprar_acosLongos_selecaoItens_selecao_pesquisar_produtos',
                      screenName: 'LG:Comprar:AcosLongos:SelecaoItens:Selecao',
                      filterInfo: `#codEmr:${emitterId ?? 'not_set'}#codRbd:${
                        shoppingCartState.receiverId
                          ? shoppingCartState.receiverId
                          : 'not_set'
                      }#codMat:${codMat ?? 'not_set'}#tpCod:${
                        codeType === 'SKU' ? 'cod_gerdau' : 'cod_interno'
                      }#flt:${searchInputValue ?? 'not_set'}`,
                      event: 'click',
                      orderCliente: shoppingCartState.customerOrder
                        ? shoppingCartState.customerOrder
                        : 'not_set'
                    })
                  );

                  search('search');
                }}
                disabled={
                  (searchItemArray.length < 1 && searchInputValue === '') ||
                  orderItemSearch.searchingItems
                }
              >
                Pesquisar produtos
              </Button>
            </div>
          </SearchItemInputContainer>
        </div>
      </Container>
      {!orderItemSearch.searchedItems.length ? (
        <FooterButtonContainer
          itensQuantity={searchItemArray.length}
          notFoundItems={0}
          onConfirmClick={() => search('searchAdvance')}
          disabledConfirmButton={
            searchItemArray.length === 0 || orderItemSearch.searchingItems
          }
        ></FooterButtonContainer>
      ) : (
        <></>
      )}
    </>
  );
};

export default SearchItem;
